import React, { useContext, useState } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Box,
  FormHelperText,
  TextField,
} from '@mui/material';
import { DialogWrapper } from '../../../presentation-components/display-elements-components/dialog-wrapper';
import ReportPeriodService from '../../../../shared/services/report-period/report-period.service';
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';

interface ChangeConversionYearDialogProps {
  open: boolean;
  conversionSourceName: string;
  conversionSourceId: string;
  currentYear: string;
  availableYears: string[];
  reportPeriodId: string;
  onClose: () => void;
  onSuccess: () => void;
}

export const ChangeConversionYearDialog: React.FC<ChangeConversionYearDialogProps> = ({
  open,
  conversionSourceName,
  conversionSourceId,
  currentYear,
  availableYears,
  reportPeriodId,
  onClose,
  onSuccess,
}) => {
  const [selectedYear, setSelectedYear] = useState<string>(currentYear);
  const [confirmationText, setConfirmationText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const reportPeriodService = new ReportPeriodService();
  const userNotificationContext = useContext(UserNotificationsContext);

  const handleClose = () => {
    setSelectedYear(currentYear);
    setConfirmationText('');
    setErrorMessage('');
    onClose();
  };

  const handleSubmit = async () => {
    if (!selectedYear || !conversionSourceId) {
      setErrorMessage('Please select a valid year');
      return;
    }

    if (confirmationText.trim().toLowerCase() !== 'confirm') {
      setErrorMessage('Please type "confirm" to proceed');
      return;
    }

    if (selectedYear === currentYear) {
      handleClose();
      return;
    }

    try {
      setIsSubmitting(true);
      setErrorMessage('');

      const response = await reportPeriodService.updateConversionYear(reportPeriodId, {
        conversionSourceId,
        newYear: selectedYear,
      });

      if (response.success) {
        const jobId = response.job?.id;

        userNotificationContext.openSnackbar({
          open: true,
          message: 'Recalculating emissions with new conversion factors...',
          duration: 0,
          backgroundColor: 'primary.main',
        });

        onSuccess();
        handleClose();

        // Poll job status
        if (jobId) {
          const pollInterval = setInterval(async () => {
            try {
              const jobStatus = await reportPeriodService.getConversionYearRecalculationJobStatus(jobId);

              if (jobStatus.status === 'COMPLETED') {
                clearInterval(pollInterval);
                setTimeout(() => {
                  userNotificationContext.openSnackbar({
                    open: true,
                    message: `Conversion year update complete (${jobStatus.recalculatedCount} emissions recalculated)`,
                    duration: 5000,
                    backgroundColor: 'success.main',
                  });
                }, 100);
              } else if (jobStatus.status === 'FAILED') {
                clearInterval(pollInterval);
                setTimeout(() => {
                  userNotificationContext.openSnackbar({
                    open: true,
                    message: jobStatus.errorMessage ? `Conversion year update failed: ${jobStatus.errorMessage}` : 'Conversion year update failed',
                    duration: 8000,
                    backgroundColor: 'error.main',
                  });
                }, 100);
              }
            } catch (err) {
              console.error('Error polling job status:', err);
            }
          }, 2000);
        }
      } else {
        setErrorMessage(response.message || 'Failed to queue conversion year update');
      }
    } catch (error: any) {
      console.error('Error updating conversion year:', error);
      setErrorMessage(error?.response?.data?.message || 'There was an error, please try again');
    } finally {
      setIsSubmitting(false);
    }
  };

  const isDisabled = selectedYear === currentYear || confirmationText.trim().toLowerCase() !== 'confirm';

  return (
    <DialogWrapper
      open={open}
      onClose={handleClose}
      title="Change Conversion Year"
      cancelHandler={handleClose}
      submitHandler={handleSubmit}
      isSubmitting={isSubmitting}
      disableSaveButton={isDisabled}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box>
          <strong>Conversion Factor Source:</strong> {conversionSourceName}
        </Box>

        <FormControl fullWidth error={!!errorMessage}>
          <InputLabel>Conversion Year</InputLabel>
          <Select
            value={selectedYear}
            label="Conversion Year"
            onChange={(e) => {
              setSelectedYear(e.target.value);
              setErrorMessage('');
            }}
            disabled={isSubmitting}
          >
            {availableYears.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
          {selectedYear === currentYear && availableYears.length > 1 && (
            <FormHelperText>Current year selected</FormHelperText>
          )}
        </FormControl>

        <Alert severity="info">
          This will recalculate all emissions in this reporting period using the new conversion factors. The update is processed in the background.
        </Alert>

        <TextField
          label='Type "confirm" to proceed'
          value={confirmationText}
          onChange={(e) => {
            setConfirmationText(e.target.value);
            setErrorMessage('');
          }}
          fullWidth
          disabled={isSubmitting}
        />
      </Box>
    </DialogWrapper>
  );
};
